import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { quickLinks } from '@/mocks/quickLinks';
import { featuredProducts } from '@/mocks/products';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function HomePage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
    { q: t('faq.q6'), a: t('faq.a6') },
  ];

  const filteredCats = categories.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const valueCards = [
    { icon: 'ri-search-2-line', title: t('value.find'), desc: t('value.findDesc') },
    { icon: 'ri-bar-chart-grouped-line', title: t('value.compare'), desc: t('value.compareDesc') },
    { icon: 'ri-file-list-3-line', title: t('value.process'), desc: t('value.processDesc') },
    { icon: 'ri-shield-check-line', title: t('value.safety'), desc: t('value.safetyDesc') },
    { icon: 'ri-global-line', title: t('value.lang'), desc: t('value.langDesc') },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent-100/30 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
        </div>
        <div className="container-main relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                {t('hero.eyebrow')}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight mb-5">
                {t('hero.title')}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="/cssbuy-spreadsheet/" className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
                  {t('hero.ctaPrimary')}
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a href="/cssbuy-spreadsheet/" className="inline-flex items-center gap-2 px-5 py-3 border border-foreground-200 text-foreground-800 text-sm font-medium rounded-full hover:bg-background-100 transition-colors whitespace-nowrap">
                  {t('hero.ctaSecondary')}
                </a>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-5 py-3 bg-accent-500 text-white text-sm font-medium rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap">
                  {t('hero.ctaMain')}
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
              <p className="mt-5 text-xs text-foreground-500">
                {t('hero.trust')}
              </p>
            </div>
            <div className="relative hidden lg:block">
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-table-2 text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t('hero.cardTitle')}</p>
                    <p className="text-xs text-foreground-500">{t('hero.organizedResources')}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {categories.slice(0, 5).map((cat) => (
                    <div key={cat.id} className="flex items-center gap-3 p-2 rounded-lg bg-background-100/50">
                      <img src={cat.image} alt="" className="w-8 h-8 rounded object-cover object-top" loading="lazy" />
                      <span className="text-sm text-foreground-700">{cat.name}</span>
                      <i className="ri-arrow-right-line ml-auto text-foreground-400 text-xs"></i>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-background-200/70 flex items-center justify-between">
                  <span className="text-xs text-foreground-500">{t('hero.categoriesCount')}</span>
                  <a href="/cssbuy-spreadsheet/" className="text-xs text-primary-500 font-medium">{t('hero.viewAll')}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2">{t('search.title')}</h2>
          </div>
          <div className="max-w-xl mx-auto relative mb-8">
            <i className="ri-search-2-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('search.placeholder')}
              className="w-full pl-11 pr-4 py-3.5 bg-background-50 rounded-full border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300/50 focus:border-primary-300 transition-all"
            />
          </div>

          {/* Quick Navigation Links with Images */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {quickLinks.map((link) => (
              <a
                key={link.id}
                href={link.url}
                className="group flex flex-col items-center gap-2 p-2.5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/50 hover:-translate-y-0.5 transition-all"
              >
                <div className="w-full aspect-square rounded-lg overflow-hidden">
                  <img src={link.image} alt={t(link.nameKey)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="flex items-center gap-1.5">
                  <i className={`${link.icon} text-primary-500 text-xs`}></i>
                  <span className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap">{t(link.nameKey)}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-14 md:py-20">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">{t('categories.title')}</h2>
            <a href="/categories/" className="text-sm text-primary-500 font-medium hover:text-primary-600">{t('categories.cta')}</a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {(search ? filteredCats : categories).map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 hover:shadow-sm transition-all"
              >
                <div className="aspect-square overflow-hidden">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">{cat.name}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="container-main">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 text-center mb-10">{t('value.title')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {valueCards.map((card, i) => (
              <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-11 h-11 mx-auto rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                  <i className={`${card.icon} text-accent-500 text-lg`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">{card.title}</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Site CTA Buttons */}
      <section className="py-12 md:py-16">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">{t('btn.cssbuySpreadsheet')}</h2>
            <p className="text-sm text-foreground-600 mb-6">
              {t('home.cta.description')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
                {t('btn.cssbuySpreadsheet')}
                <i className="ri-external-link-line"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-white text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap">
                {t('btn.buyItNow')}
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">{t('products.featuredTitle')}</h2>
            <a href="/products/" className="text-sm text-primary-500 font-medium hover:text-primary-600">{t('products.viewAll')}</a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {featuredProducts.map((product) => (
              <a
                key={product.id}
                href={product.link}
                target="_blank"
                rel="noopener nofollow"
                className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 hover:shadow-sm transition-all"
              >
                <div className="aspect-square overflow-hidden bg-background-100">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Minimal%20product%20placeholder%20icon%20on%20light%20neutral%20background%2C%20clean%20geometric%20shape%2C%20soft%20shadows&width=400&height=400&seq=product-fallback&orientation=squarish';
                    }}
                  />
                </div>
                <div className="p-3">
                  <span className="inline-block text-xs font-medium text-accent-600 bg-accent-100/50 px-2 py-0.5 rounded-full mb-1.5">
                    {product.category}
                  </span>
                  <h3 className="text-xs font-medium text-foreground-900 leading-snug mb-1.5 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {product.title}
                  </h3>
                  <p className="text-xs font-semibold text-primary-600 mb-2">
                    {product.price}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full group-hover:bg-primary-500 group-hover:text-white transition-colors">
                    {t('products.buyNow')}
                    <i className="ri-external-link-line"></i>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">{t('articles.title')}</h2>
            <a href="/articles/" className="text-sm text-primary-500 font-medium hover:text-primary-600">{t('articles.viewAll')}</a>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article) => (
              <a key={article.id} href={`/article/${article.slug}`} className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all">
                <div className="aspect-video overflow-hidden">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-accent-600 bg-accent-100/50 px-2 py-0.5 rounded-full">{article.category}</span>
                    <span className="text-xs text-foreground-500">{article.readTime} {t('articles.readTime')}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">{article.title}</h3>
                  <p className="text-xs text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
                  <span className="text-xs font-medium text-primary-500">{t('articles.readMore')}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Review */}
      <section className="py-14 md:py-20">
        <div className="container-main">
          <div className="max-w-3xl">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">{t('review.title')}</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">{t('review.text')}</p>
            <a href="/cssbuy-review/" className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-200 text-foreground-800 text-sm font-medium rounded-full hover:bg-background-100 transition-colors">
              {t('review.cta')}
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Shipping */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="container-main">
          <div className="max-w-3xl">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">{t('shipping.title')}</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">{t('shipping.text')}</p>
            <a href="/cssbuy-shipping-cost/" className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-200 text-foreground-800 text-sm font-medium rounded-full hover:bg-background-100 transition-colors">
              {t('shipping.cta')}
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-14 md:py-20">
        <div className="container-main">
          <div className="max-w-3xl">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">{t('safety.title')}</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">{t('safety.text')}</p>
            <a href="/safety/" className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-200 text-foreground-800 text-sm font-medium rounded-full hover:bg-background-100 transition-colors">
              {t('safety.cta')}
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="container-main">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-8">{t('faq.title')}</h2>
          <div className="max-w-2xl space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-foreground-500 shrink-0`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">{t('finalCta.title')}</h2>
            <p className="text-sm md:text-base text-foreground-600 mb-8">{t('finalCta.text')}</p>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <a href="/cssbuy-spreadsheet/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
                {t('finalCta.explore')}
                <i className="ri-arrow-right-line"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white text-sm font-medium rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap">
                {t('finalCta.visit')}
                <i className="ri-external-link-line"></i>
              </a>
            </div>
            <p className="text-xs text-foreground-500 italic">{t('finalCta.disclaimer')}</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}